import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { HttpInternalService } from 'src/app/data/service/http.service';

/** GET api/transactions/daily-sync: the organisation's daily sync, and whether its plan includes it. */
export interface DailySyncState {
  entitled: boolean;
  /** The plan includes it, though this person may not switch it on (a Submitter asks an admin). */
  planIncludes: boolean;
  /** Switched on (kept through a downgrade). */
  isSwitchedOn: boolean;
  /** Switched on and running: on a plan that includes it. */
  isActive: boolean;
  runTime: string;
  accountIds: string[];
  timeZoneId: string | null;
  /** The saved timezone, else the organisation's own from Xero. */
  suggestedTimeZoneId: string | null;
  activatedUtc: string | null;
  lastRunUtc: string | null;
  lastRunStatus: 'success' | 'failed' | 'skipped' | 'off' | null;
  lastRunImported: number | null;
  lastRunMessage: string | null;
}

export interface DailySyncSaveResult {
  isActive: boolean;
  workflowId: string | null;
  workflowEnabled: boolean;
  workflowError: string | null;
  workflowWarnings: string[];
}

/** What the Import panel's daily sync card shows, the way InboxAutomatorService.mode() reads a mailbox. */
export type DailySyncMode = 'active' | 'activate' | 'ask-admin' | 'upgrade';

/**
 * The daily transaction sync: new Xero bank transactions imported every day at 10:00 in the
 * organisation's timezone, for the accounts chosen. A paid feature, switched on from the
 * Transactions screen's Import panel the way Inbox Automator is from the Emails tab.
 */
@Injectable({ providedIn: 'root' })
export class TransactionDailySyncService {

  /** Fires with the organisation id after a change, so every view of it reloads. */
  readonly changed$ = new Subject<string>();

  constructor(private http: HttpInternalService) { }

  static mode(state: DailySyncState | null): DailySyncMode {
    if (!state) { return 'upgrade'; }
    if (state.isActive) { return 'active'; }
    if (state.entitled) { return 'activate'; }
    if (state.planIncludes) { return 'ask-admin'; }
    return 'upgrade';
  }

  /** The one action offered beside it: what clicking does in each mode. */
  static actionLabel(state: DailySyncState | null): string {
    switch (TransactionDailySyncService.mode(state)) {
      case 'active': return 'Manage';
      case 'activate': return 'Set up';
      case 'ask-admin': return 'Details';
      default: return 'Upgrade';
    }
  }

  /** One line: what it does when off, what it is doing when on. */
  static describe(state: DailySyncState | null): string {
    const s = state;
    if (!s) { return 'Import new transactions automatically every morning'; }
    if (TransactionDailySyncService.mode(s) !== 'active') {
      return `Import new transactions from Xero automatically, every day at ${s.runTime}`;
    }
    const n = (s.accountIds || []).length;
    const zone = (s.timeZoneId || '').replace('_', ' ');
    let line = `Every day at ${s.runTime} ${zone ? '(' + zone + ')' : ''} · ${n} account${n === 1 ? '' : 's'}`;
    if (s.lastRunUtc && s.lastRunStatus === 'success') {
      const imported = s.lastRunImported || 0;
      line += ` · last run ${imported === 0 ? 'found nothing new' : imported + ' new'}`;
    } else if (s.lastRunUtc && s.lastRunStatus === 'failed') {
      line += ' · last run did not finish';
    }
    return line;
  }

  state(organizationId: string): Observable<DailySyncState> {
    return this.http.getRequest<DailySyncState>(`transactions/daily-sync?organizationId=${encodeURIComponent(organizationId)}`);
  }

  /** Switches it on, or saves new accounts or timezone while it is on. */
  activate(organizationId: string, accountIds: string[], timeZoneId: string): Observable<DailySyncSaveResult> {
    return this.http.postRequest<DailySyncSaveResult>('transactions/daily-sync/activate', { organizationId, accountIds, timeZoneId })
      .pipe(tap(() => this.changed$.next(organizationId)));
  }

  deactivate(organizationId: string): Observable<DailySyncSaveResult> {
    return this.http.postRequest<DailySyncSaveResult>('transactions/daily-sync/deactivate', { organizationId })
      .pipe(tap(() => this.changed$.next(organizationId)));
  }
}
